import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Eye, MousePointer, Target, DollarSign, TrendingUp, Clock, Download, Info, ArrowUp, ArrowDown, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CPDCInfoButton } from "@/components/ui/MetricInfo";
import { MetricCard, CHART_COLORS } from "@/components/MetricCard";
import { REPORTABLE_STATUSES } from "@/lib/campaignStatus";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { format } from "date-fns";

const COLORS = ['#3B82F6', '#F97316', '#EAB308', '#A855F7', '#22C55E'];

// Info tooltip component
const InfoTooltip = ({ content }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button className="ml-1 text-muted-foreground hover:text-foreground transition-colors">
          <Info className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="text-sm">{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// A campaign only has analytics once it has been live. DRAFT never has.


// The All Campaigns Performance table (round 9, TP-25).
//
// Declared rather than hand-written: it was eight near-identical eleven-line <th>
// blocks, which is how it came to be missing Redeems and Followers - adding a column
// meant copying eleven lines twice, once into the header and once into the body, and
// keeping their order in step by eye.
//
// Joel's "gaps/deadspace here not utilised correctly": two left-aligned text columns
// took the width while the numeric ones crowded the right. The widths are now stated.
//
// Spend keeps its place and Budget joins it at the end, which is what the ask means -
// a spend figure with no budget beside it does not say whether it is a lot.
const PERFORMANCE_COLUMNS = [
  {
    key: "name",
    label: "Campaign",
    align: "left",
    width: "w-[28%]",
    render: (c) => (
      <div className="flex items-center gap-2">
        <span className="font-medium truncate">{c.name}</span>
        <Badge
          variant="outline"
          className={
            c.status === "ACTIVE" ? "border-green-500/30 text-green-500"
              : c.status === "PAUSED" ? "border-amber-500/30 text-amber-500"
                : "border-muted-foreground/30"
          }
        >
          {c.status}
        </Badge>
      </div>
    ),
  },
  {
    key: "start_at",
    label: "Started",
    align: "left",
    width: "w-[12%]",
    cellClass: "text-muted-foreground whitespace-nowrap",
    render: (c) => (c.start_at ? format(new Date(c.start_at), "d MMM yyyy") : "-"),
  },
  { key: "impressions", label: "Views", align: "right", render: (c) => (c.impressions || 0).toLocaleString() },
  { key: "clicks", label: "Clicks", align: "right", render: (c) => (c.clicks || 0).toLocaleString() },
  { key: "arrivals", label: "Delivered", align: "right", render: (c) => (c.arrivals || 0).toLocaleString() },
  { key: "redemptions", label: "Redeems", align: "right", render: (c) => (c.redemptions || 0).toLocaleString() },
  { key: "followers", label: "Followers", align: "right", render: (c) => (c.followers || 0).toLocaleString() },
  {
    key: "spend",
    label: "Spend",
    align: "right",
    cellClass: "font-medium",
    render: (c) => `$${(c.spend || 0).toLocaleString()}`,
  },
  {
    key: "budget",
    label: "Budget",
    align: "right",
    // Null is not zero. A campaign with no budget is uncapped, and "$0" would read as
    // one that cannot spend.
    render: (c) => (c.budget == null ? <span className="text-muted-foreground">No cap</span>
      : `$${Number(c.budget).toLocaleString()}`),
  },
];

export default function Analytics() {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [loading, setLoading] = useState(true);
  const [campaignPerformance, setCampaignPerformance] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'spend', direction: 'desc' });
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState("30"); // days: 7, 30, 60, 90, custom
  const [customDays, setCustomDays] = useState(30);
  // Raw text of the custom-days field. Kept separate from `customDays` (which drives the
  // fetch) so typing doesn't refetch on every keystroke — committed on blur/Enter. (#6)
  const [customDaysInput, setCustomDaysInput] = useState("30");
  const [hourlyData, setHourlyData] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedCampaign, dateRange, customDays]);

  const getDaysValue = () => {
    if (dateRange === "custom") return customDays;
    return parseInt(dateRange);
  };

  // Commit the typed custom-days value (clamped 1–730). Only this updates `customDays`,
  // so the analytics refetch fires once on blur/Enter rather than on every keystroke. (#6)
  const commitCustomDays = () => {
    const v = Math.min(730, Math.max(1, parseInt(customDaysInput, 10) || 30));
    setCustomDaysInput(String(v));
    setCustomDays(v);
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const days = getDaysValue();
      
      if (selectedCampaign === "all") {
        const [analyticsRes, timeseriesRes, campaignsRes, hourlyRes, perfRes] = await Promise.all([
          axios.get(`${API}/analytics/summary?days=${days}`, { headers }),
          axios.get(`${API}/analytics/timeseries?days=${days}`, { headers }),
          axios.get(`${API}/campaigns`, { headers }),
          axios.get(`${API}/analytics/hourly?days=${days}`, { headers }),
          axios.get(`${API}/analytics/campaign-performance?days=${days}`, { headers })
        ]);
        setAnalytics(analyticsRes.data);
        setTimeseries(timeseriesRes.data);
        setCampaigns(campaignsRes.data);
        setHourlyData(hourlyRes.data);
        setCampaignPerformance(perfRes.data);
      } else {
        const campaignAnalytics = await axios.get(`${API}/analytics/campaigns/${selectedCampaign}`, { headers });
        setAnalytics(campaignAnalytics.data.summary);
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/analytics/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `booma_analytics_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported successfully", {
        description: "Your analytics data has been downloaded"
      });
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed", {
        description: "Unable to export analytics data. Please try again."
      });
    } finally {
      setExporting(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass-heavy rounded-lg p-3 border border-border shadow-xl">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.name === 'spend' ? `$${entry.value.toFixed(2)}` : entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Spend breakdown by metric type - real per-metric spend from API only
  const metricBreakdown = [
    { name: 'Views', value: analytics?.spend_views || 0, color: '#3B82F6' },
    { name: 'Clicks', value: analytics?.spend_clicks || 0, color: '#F97316' },
    { name: 'Redeems', value: analytics?.spend_redeem || 0, color: '#22C55E' },
    { name: 'Delivered Customers', value: (analytics?.spend_cpdc_direct || 0) + (analytics?.spend_cpdc_proximity || 0) + (analytics?.spend_cpdc_prompted || 0), color: '#A855F7' },
  ];

  // 24-hour zero-filled placeholder so the "Time of Day" chart renders its empty axis/label
  // skeleton when there's no data yet, instead of a blank box. (#5)
  const EMPTY_HOURLY = Array.from({ length: 24 }, (_, h) => ({
    hour: h,
    hourLabel: `${(h % 12) || 12}${h < 12 ? 'AM' : 'PM'}`,
    impressions: 0,
  }));

  // Sort campaigns
  const sortedCampaigns = [...campaignPerformance].sort((a, b) => {
    if (sortConfig.direction === 'asc') {
      return a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
    }
    return a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
  });

  const topCampaigns = sortedCampaigns.slice(0, 5);
  const bottomCampaigns = [...campaignPerformance].sort((a, b) => a.spend - b.spend).slice(0, 5);
  // Round 9, TP-23. Ranking campaigns that have all spent nothing sorts on nothing and
  // then presents the order as a finding, which is what "$0 / 0 arrivals" on both
  // cards actually meant. Compare only when there is something to compare.
  const hasDelivery = campaignPerformance.some(
    c => (c.spend || 0) > 0 || (c.impressions || 0) > 0 || (c.clicks || 0) > 0
  );

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Performance metrics and insights
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Date Range Selector */}
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <Select value={dateRange} onValueChange={(v) => { setDateRange(v); if (v === "custom") setCustomDaysInput(String(customDays)); }}>
              <SelectTrigger className="w-36" data-testid="date-range-filter">
                <SelectValue placeholder="Date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 6 months</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            {dateRange === "custom" && (
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="1"
                  max="730"
                  value={customDaysInput}
                  onChange={(e) => setCustomDaysInput(e.target.value)}
                  onBlur={commitCustomDays}
                  onKeyDown={(e) => { if (e.key === "Enter") { commitCustomDays(); e.currentTarget.blur(); } }}
                  className="w-16 h-9 px-2 rounded-md border border-input bg-background text-sm"
                />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
            )}
          </div>
          
          <Button variant="outline" onClick={handleExportCSV} disabled={exporting} data-testid="export-csv-btn">
            {exporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </>
            )}
          </Button>
          <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
            <SelectTrigger className="w-64" data-testid="campaign-filter">
              <SelectValue placeholder="Select campaign" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Campaigns</SelectItem>
              {/* Campaigns that have actually run. A draft has never been delivered, so
                  selecting one shows a page of zeros that reads as a reporting failure -
                  round 2: "draft/saved campaigns showing up as active campaigns on
                  dashboard and on analytics tab." */}
              {campaigns
                .filter(c => REPORTABLE_STATUSES.includes(c.status))
                .map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Metrics. Six since round 9 (TP-24): Redeems was missing, and the
          MetricCard type for it already existed - the endpoint simply had no count to
          put in it. */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Views */}
        <MetricCard 
          type="impressions" 
          value={analytics?.impressions} 
          testId="analytics-impressions"
        />
        
        {/* Clicks + Saved Offers nested (#1/#4; CTR removed per P2-1) */}
        <MetricCard
          type="clicks"
          value={analytics?.clicks}
          savedOffers={analytics?.deals_saved || 0}
          testId="analytics-clicks"
        />

        {/* Followers (#3) */}
        <MetricCard
          type="favorites"
          value={analytics?.favorite_count || 0}
          testId="analytics-followers"
        />
        
        {/* Delivered Customers */}
        <MetricCard 
          type="cpdc" 
          value={(analytics?.cpdc_direct || 0) + (analytics?.cpdc_proximity || 0) + (analytics?.cpdc_prompted || 0) || analytics?.cpdc || 0}
          direct={analytics?.cpdc_direct || 0}
          proximity={analytics?.cpdc_proximity || 0}
          prompted={analytics?.cpdc_prompted || 0}
          testId="analytics-cpdc"
        />
        
        {/* Redeems (round 9, TP-24). Beside Saved Offers in meaning: a save is a
            customer keeping an offer, a redeem is them turning up and using it. */}
        <MetricCard
          type="redeems"
          value={analytics?.redemptions || 0}
          testId="analytics-redeems"
        />

        {/* Total Spend */}
        <MetricCard 
          type="spend" 
          value={analytics?.spend}
          testId="analytics-spend"
        />
      </div>

      {/* Best and lowest performing (round 9, TP-23).
          Names and rank only. Every figure that was here duplicated the table below,
          and with no delivery data they were all zero, so the two cards showed the
          same campaigns with the same numbers under opposite headings. */}
      {campaignPerformance.length > 0 && (
        hasDelivery ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[
              {
                key: "best",
                icon: <ArrowUp className="h-5 w-5 text-green-500" />,
                title: "Best performing campaigns",
                description: "Ranked by spend delivered.",
                rows: topCampaigns,
                pill: "bg-green-500/20 text-green-500",
              },
              {
                key: "lowest",
                icon: <ArrowDown className="h-5 w-5 text-amber-500" />,
                title: "Lowest performing campaigns",
                description: "May need optimisation.",
                rows: bottomCampaigns,
                pill: "bg-amber-500/20 text-amber-500",
              },
            ].map(panel => (
              <Card key={panel.key} className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {panel.icon}
                    {panel.title}
                  </CardTitle>
                  <CardDescription>{panel.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {panel.rows.map((campaign, index) => (
                      <div
                        key={campaign.id}
                        className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30"
                      >
                        <span className={`w-6 h-6 shrink-0 rounded-full text-xs font-bold flex items-center justify-center ${panel.pill}`}>
                          {index + 1}
                        </span>
                        <p className="font-medium text-sm truncate">{campaign.name}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* Round 12, FP-24: "Analytics page cannot just have the text box in my
             circle it needs to explain what it is".
             It was a sentence in a box with no heading, so a merchant seeing it for
             the first time could not tell what the box WOULD hold, only that it held
             nothing. An empty state has to name the thing that is empty. */
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Campaign comparison
              </CardTitle>
              <CardDescription>
                Which of your campaigns are working hardest, and which are not earning
                their spend.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Nothing to compare yet. Once a campaign has started delivering, your
                best and lowest performing campaigns are ranked here side by side, so
                you can see where to put more budget and where to change the offer,
                the creative or the targeting.
              </p>
            </CardContent>
          </Card>
        )
      )}

      {/* All Campaigns Table */}
      {campaignPerformance.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>All Campaigns Performance</CardTitle>
            <CardDescription>Click column headers to sort</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    {PERFORMANCE_COLUMNS.map(col => (
                      <th
                        key={col.key}
                        className={`p-3 text-xs text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground ${col.align === "right" ? "text-right" : "text-left"} ${col.width || ""}`}
                        onClick={() => handleSort(col.key)}
                      >
                        <span className={`flex items-center gap-1 ${col.align === "right" ? "justify-end" : ""}`}>
                          {col.label} <SortIcon columnKey={col.key} />
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedCampaigns.map((campaign) => (
                    <tr key={campaign.id} className="border-b border-border/50 hover:bg-secondary/20">
                      {PERFORMANCE_COLUMNS.map(col => (
                        <td
                          key={col.key}
                          className={`p-3 ${col.align === "right" ? "text-right tabular-nums" : ""} ${col.cellClass || ""}`}
                        >
                          {col.render(campaign)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Performance Chart */}
        <Card className="bg-card border-border lg:col-span-2" data-testid="timeseries-chart">
          <CardHeader>
            <CardTitle>Performance Trend</CardTitle>
            <CardDescription>
              {dateRange === "7" ? "Last 7 days" :
               dateRange === "30" ? "Last 30 days" :
               dateRange === "60" ? "Last 60 days" :
               dateRange === "90" ? "Last 90 days" :
               dateRange === "180" ? "Last 6 months" :
               dateRange === "365" ? "Last year" :
               dateRange === "custom" ? `Last ${customDays} days` : "Last 30 days"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeseries}>
                  <defs>
                    <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F97316" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#F97316" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorDelivered" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#A855F7" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#A855F7" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorRedeems" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#A1A1AA"
                    fontSize={11}
                    tickFormatter={(value) => format(new Date(value), "MMM d")}
                    interval="preserveStartEnd"
                  />
                  <YAxis yAxisId="left" stroke="#3B82F6" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <YAxis yAxisId="right" orientation="right" stroke="#A1A1AA" fontSize={11} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="impressions"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorImpressions)"
                    name="Views"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="clicks"
                    stroke="#F97316"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorClicks)"
                    name="Clicks"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="delivered_customers"
                    stroke="#A855F7"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorDelivered)"
                    name="Delivered Customers"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="redeems"
                    stroke="#22C55E"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorRedeems)"
                    name="Redeems"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Spend Breakdown */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Spend Breakdown</CardTitle>
            <CardDescription>By billing metric</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {metricBreakdown.some(m => m.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={metricBreakdown.filter(m => m.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {metricBreakdown.filter(m => m.value > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0];
                        return (
                          <div className="glass-heavy rounded-lg px-3 py-2 border border-border shadow-xl">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: data.payload.color }} />
                              <span className="text-sm font-medium text-foreground">{data.payload.name}</span>
                            </div>
                            <p className="text-sm font-bold text-foreground mt-1">
                              ${data.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <div className="rounded-full border-[18px] border-muted/40" style={{ width: 150, height: 150 }} />
                  <p className="text-muted-foreground text-sm">No spend data yet - your breakdown will appear here</p>
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {metricBreakdown.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-sm text-muted-foreground">
                    {item.name}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Heatmap */}
      <Card className="bg-card border-border" data-testid="hourly-chart">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle>Time of Day Performance</CardTitle>
              <CardDescription>Hourly view distribution</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            {hourlyData.some(d => d.impressions > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis 
                  dataKey="hourLabel" 
                  stroke="#A1A1AA" 
                  fontSize={10}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                />
                <YAxis stroke="#A1A1AA" fontSize={11} />
                <RechartsTooltip 
                  contentStyle={{ 
                    backgroundColor: '#121214', 
                    border: '1px solid #27272A',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(label) => `Hour: ${label}`}
                />
                <Bar dataKey="impressions" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Views" />
              </BarChart>
            </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={EMPTY_HOURLY}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                  <XAxis dataKey="hourLabel" stroke="#A1A1AA" fontSize={10} interval={0} angle={-45} textAnchor="end" height={50} />
                  <YAxis stroke="#A1A1AA" fontSize={11} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
